import React, { useState } from "react";
import axios from "axios";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Modal from '../ui/Modal';
import ReadyHint from '../ui/ReadyHint';
import Button from '../ui/Button';
import PhoneInput from '../ui/PhoneInput';
import { isValidMobile } from '../../utils/phone';
import { clearPendingBookingCache } from '../../utils/pendingBooking';

// `summary` (optional) is what the visitor is about to lose or get: the checkout passes the price they hold and the offer timer.
const LoginPopup = ({ onClose, proceedToCoupen, onClose1, summary = null }) => {
  const [mobile, setMobile] = useState(''); // the 10 digits, without +91
  const [showMobileErrors, setShowMobileErrors] = useState(false);
  const [isOtpSend, setIsOtpSend] = useState(false);
  const [otp, setOtp] = useState('');
  const userType = sessionStorage.getItem('userType')
  const [verifyOtpToken, setVerifyOtpToken] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  // The buttons stay off until what they need is there; the line under the first button says what is left.
  const phoneMissing = [];
  if (!isValidMobile(mobile)) phoneMissing.push('a valid 10-digit mobile number');
  const otpReady = /^\d{4,8}$/.test(String(otp).trim());

  const senOtpEmail = async (e) => {
    e.preventDefault();
    if (!isValidMobile(mobile)) {
      setShowMobileErrors(true);
      toast('Please enter a valid 10-digit mobile number', { type: 'warning' });
      return;
    }
    if (isSendingOtp) return;
    setIsSendingOtp(true);
    try {
      let response = await axios.post(`${process.env.REACT_APP_SERVER_URL}/send-otp`, { phone:mobile });
      if (response.data.message) {
        toast(`${response.data.message}`, { type: 'success' });
        setVerifyOtpToken(response.data.otpVerifyToken);
        setIsOtpSend(true);
      }
    } catch (error) {
      console.error(error);
      // e.g. "Too many OTP requests..." (429) or "Please enter a valid mobile number" (400)
      toast(error?.response?.data?.message || 'Unable to send the OTP. Please try again.', { type: 'error' });
    } finally {
      setIsSendingOtp(false);
    }
  };

  const createNewAccount = async (e) => {
    e.preventDefault();
    if (isVerifyingOtp || !otpReady) return;
    setIsVerifyingOtp(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/user-verify`,
        { otp, userType },
        {
          headers: {
            Authorization: `Bearer ${verifyOtpToken}`,
          },
        }
      );
      if (response.data.loginToken) {
        localStorage.setItem('loginToken', response.data.loginToken);
        clearPendingBookingCache(); // this account may have a payment awaiting a session time
        toast(`${response.data.message}`, { type: 'success' });
        if (!onClose1) {
          onClose();
        }
        if (proceedToCoupen) {
          proceedToCoupen();
        }
        if (onClose1) {
          onClose1();
        }
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      toast(`${errorMessage}`, { type: 'error' });
      console.error(error);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleKeyPress1 = (event) => {
    if (event.key === 'Enter') {
      createNewAccount(event);
    }
  };

  const inputClass =
    'w-full rounded-none border border-forest/15 bg-white px-4 py-3.5 text-[15px] text-forest-deepest placeholder:text-forest-deepest/35 focus:border-brand-accent focus:outline-none';

  return (
    <>
      <Modal onClose={onClose}>
        {!isOtpSend ? (
          <form onSubmit={senOtpEmail}>
            <h2 className="text-center font-serif text-2xl italic text-forest-deepest">
              {summary ? 'Log in to continue' : 'Welcome to Bhavishyavani'}
            </h2>
            <p className="mt-2 text-center text-sm text-forest-deepest/65">
              Log in with your mobile number to receive your booking information.
            </p>
            {summary && <div className="mt-4">{summary}</div>}
            <div className="my-5" />
            <PhoneInput
              id="login-mobile"
              value={mobile}
              onChange={setMobile}
              showErrors={showMobileErrors}
              inputClassName={inputClass}
              prefixClassName="border-forest/15 bg-canvas text-forest-deepest/70"
              placeholder="10-digit mobile number"
              autoFocus
            />
            <Button type="submit" variant="primary-dark" className="mt-4 w-full" loading={isSendingOtp} disabled={phoneMissing.length > 0}>
              Send my code by SMS
            </Button>
            <ReadyHint missing={phoneMissing} className="mt-2 text-center" />
            <p className="mt-3 text-center text-[11px] text-forest-deepest/50">
              No password. No spam.
            </p>
            <button type="button" onClick={onClose} className="mx-auto mt-2 block text-xs text-forest-deepest/45 underline underline-offset-2 hover:text-forest-deepest/70">
              Not now
            </button>
          </form>
        ) : (
          <form onSubmit={createNewAccount}>
            <h2 className="text-center font-serif text-2xl italic text-forest-deepest">Enter your code</h2>
            <p className="mt-3 text-center text-sm text-forest-deepest/65">
              Code sent to <strong>+91 {mobile}</strong> by SMS.
            </p>
            {summary && <div className="mt-4">{summary}</div>}
            <div className="my-5" />
            <input
              value={otp}
              placeholder="Enter code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={8}
              autoFocus
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              onKeyPress={handleKeyPress1}
              className={inputClass}
              required
            />
            <Button type="submit" variant="primary-dark" className="mt-4 w-full" loading={isVerifyingOtp} disabled={!otpReady}>
              {summary ? 'Verify and continue to payment' : 'Verify and continue'}
            </Button>
            <div className="mt-3 flex items-center justify-between text-xs text-forest-deepest/55">
              <button type="button" onClick={() => { setIsOtpSend(false); setOtp(''); }} className="underline underline-offset-2 hover:text-forest-deepest/80">Wrong number?</button>
              <button type="button" onClick={onClose} className="underline underline-offset-2 hover:text-forest-deepest/80">Not now</button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
};

export default LoginPopup;
